import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';

import { MarcheAlerte } from '../models/marche-alerte.model';
import { IMarcheService } from './imarche.service';
import { Marche } from '../models/marche.model';
import {
  MarcheInput2,
  MarcheOutput,
  MarcheOutput2,
  MarcheReconductionInput,
  MarcheAlerteInput,
  MarcheReconductionOutput3,
  MarcheOutput3,
  MarcheAvenantOutput,
  MarchesService,
  MarchesBPUService,
  MarchesUoService,
  DocumentsService,
  MarcheTraitantInput,
  InlineObject7,
  MarcheTraitantTypeEnum,
  MarcheUoInput2,
  MarcheBpuInput
} from '../../../../openapi/build';
import { MarcheReconduction } from '../models/marche-reconduction.model';
import { MarcheResponsable } from '../models/marche-responsable.model';
import { MarcheTraitant } from '../models/marche-traitant.model';
import { NotificationService } from '../../services/alerte/notification.service';
import { MarcheDocument } from '../models/marche-document.model';
import { MarcheBpuUo, MarcheBpu } from '../models/marche-bpu.model';
import { MarcheBpuRevision } from '../models/marche-bpu-revision.model';

@Injectable({
  providedIn: 'root'
})
export class MarcheAdapterService implements IMarcheService {
  constructor(
    private readonly marcheApi: MarchesService,
    private readonly marchesServiceApi: MarchesService,
    private readonly marchesBPUService: MarchesBPUService,
    private readonly marchesUoService: MarchesUoService,
    private readonly documentsService: DocumentsService,
    private readonly notificationService: NotificationService
  ) {}

  mettreAJourMarcheAdministratif(marche: Marche): Observable<Marche> {
    const marcheInput: MarcheInput2 = Marche.adaptToApi(marche);
    return this.marchesServiceApi.putApiMarcheUpdate(marche.uuid!, marcheInput).pipe(map(response => Marche.adapt(response)));
  }

  enregistrerMarcheReconduction(marcheUuid: string, reconduction: MarcheReconductionInput): Observable<MarcheReconduction> {
    return this.marchesServiceApi.postPostReconduction(marcheUuid, reconduction).pipe(
      map((response: MarcheReconductionOutput3): MarcheReconduction => {
        return MarcheReconduction.adapt(response);
      })
    );
  }

  modifierMarcheReconduction(
    marcheUuid: string,
    reconductionUuid: string,
    reconduction: MarcheReconduction
  ): Observable<MarcheReconduction> {
    return this.marchesServiceApi
      .putPutReconduction(marcheUuid, reconductionUuid, MarcheReconduction.toMarcheReconductionInput(reconduction))
      .pipe(
        map((response: MarcheReconductionOutput3): MarcheReconduction => {
          return MarcheReconduction.adapt(response);
        })
      );
  }

  supprimerReconduction(marcheUuid: string, reconductionUuid: string): Observable<any> {
    return this.marchesServiceApi.deleteDeleteReconduction(marcheUuid, reconductionUuid);
  }
  recupererAlerteMarche(uuid: string): Observable<MarcheAlerte> {
    // Deprecated/alias: use getMarcheAlerte
    return this.getMarcheAlerte(uuid);
  }

  enregistrerAlerteMarche(MarcheUuid: string, marche: Partial<MarcheAlerteInput>): Observable<MarcheAlerte> {
    if (!MarcheUuid) {
      return throwError((): Error => new Error('uuid du marché requis pour enregistrer une alerte.'));
    }
    const input: MarcheAlerteInput = {
      seuilAlerte: marche.seuilAlerte ?? null,
      alerteDateFinPeriode: marche.alerteDateFinPeriode ? this.formatDateGMT2(marche.alerteDateFinPeriode) : null,
      alerteDateFinDeRennonciation: marche.alerteDateFinDeRennonciation ? this.formatDateGMT2(marche.alerteDateFinDeRennonciation) : null
    };
    return this.marchesServiceApi.putApiMarcheAlerteSave(MarcheUuid, input).pipe(
      map((res: MarcheOutput): MarcheAlerte => {
        this.notificationService.successEdit();
        return MarcheAlerte.adapt(res.alerte);
      })
    );
  }

  formatDateGMT2 = (dateInput: any): string => {
    const date = new Date(dateInput);
    // Ajuster pour GMT+2 (ajouter 2 heures si nécessaire)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+02:00`;
  };

  getMarcheAlerte(uuid: string): Observable<MarcheAlerte> {
    return this.marchesServiceApi.getApiGetMarcheDetail(uuid).pipe(
      map((response: Array<MarcheOutput3>): MarcheAlerte => {
        const data = Array.isArray(response) ? response[0] : response;
        const marche = Marche.adapt(data);
        return marche.alerte ?? new MarcheAlerte(uuid, 0, undefined, undefined);
      })
    );
  }

  recupererMarche(uuid: string): Observable<Marche> {
    return this.marchesServiceApi.getApiGetMarcheDetail(uuid).pipe(
      map((response: Array<MarcheOutput3>): Marche => {
        const data = Array.isArray(response) ? response[0] : response;
        return Marche.adapt(data);
      })
    );
  }

  supprimerAvenant(uuidMarche: string, uuidAvenant: string): Observable<any> {
    if (!uuidMarche || !uuidAvenant) {
      return throwError((): Error => new Error('uuidMarche et uuidAvenant sont requis.'));
    }
    return this.marchesServiceApi.deleteApiDeleteMarcheAvenantItem(uuidMarche, uuidAvenant);
  }

  supprimerAnnexe(uuidMarche: string, uuidAvenant: string): Observable<any> {
    if (!uuidMarche || !uuidAvenant) {
      return throwError((): Error => new Error('uuidMarche et uuidAvenant sont requis.'));
    }
    return throwError((): Error => new Error('Méthode non implémentée.'));
  }

  recupererAvenants(uuidMarche: string): Observable<MarcheDocument[]> {
    if (!uuidMarche) {
      return throwError((): Error => new Error('uuidMarche est requis.'));
    }
    return this.marchesServiceApi
      .getApiGetMarcheAvenants(uuidMarche, 'body', false, {
        httpHeaderAccept: 'application/json'
      })
      .pipe(
        map((avenantsApi: Array<MarcheAvenantOutput>): MarcheDocument[] =>
          avenantsApi.map((avenant: MarcheAvenantOutput): MarcheDocument => MarcheDocument.adapt(avenant))
        )
      );
  }

  detailsMarche(uuidMarche: string): Observable<Marche[]> {
    if (!uuidMarche) {
      return throwError((): Error => new Error('uuidMarche est requis.'));
    }

    return this.marchesServiceApi
      .getApiGetMarcheDetail(uuidMarche, 'body', false, {
        httpHeaderAccept: 'application/json'
      })
      .pipe(
        map((response: Array<MarcheOutput3>): Marche[] =>
          Array.isArray(response) ? response.map((m: MarcheOutput3): Marche => Marche.adapt(m)) : [Marche.adapt(response)]
        )
      );
  }

  reconductionsMarche(uuidMarche: string): Observable<MarcheReconduction[]> {
    if (!uuidMarche) {
      return throwError((): Error => new Error('uuidMarche est requis.'));
    }

    return this.marchesServiceApi
      .getApiGetMarcheReconductions(uuidMarche, 'body', false, {
        httpHeaderAccept: 'application/json'
      })
      .pipe(map((res: Array<MarcheReconductionOutput3>): MarcheReconduction[] => res as MarcheReconduction[]));
  }

  recupererLesMarches(): Observable<Marche[]> {
    return this.marchesServiceApi
      .getApiMarcheList('body', false, {
        httpHeaderAccept: 'application/json'
      })
      .pipe(map((res: Array<MarcheOutput2>): Marche[] => (res as MarcheOutput[]).map((m: MarcheOutput): Marche => Marche.adapt(m))));
  }

  ajouterResponsableTechnique(uuidMarche: string, responsable: MarcheResponsable): Observable<any> {
    const payload: InlineObject7 = {
      matricule: responsable.matricule
    };
    return this.marchesServiceApi.postApiMarcheAddResponsable(uuidMarche, payload, 'body', false, {
      httpHeaderAccept: 'application/json'
    });
  }
  supprimerResponsableTechnique(uuidMarche: string, matricule: string): Observable<any> {
    return this.marchesServiceApi.deleteApiMarcheRemoveResponsable(uuidMarche, matricule, 'body', false, {
      httpHeaderAccept: 'application/json'
    });
  }

  ajouterTraitant(uuidMarche: string, type: string, fournisseurUuid: string): Observable<MarcheTraitant> {
    const payload: MarcheTraitantInput = {
      fournisseurUuid: fournisseurUuid,
      type: type as MarcheTraitantTypeEnum
    };
    return this.marchesServiceApi.postPostTraitant(uuidMarche, payload).pipe(map(res => MarcheTraitant.adapt(res)));
  }

  supprimerTraitant(uuidMarche: string, uuidTraitant: string): Observable<MarcheTraitant> {
    return this.marchesServiceApi.deleteDeleteTraitant(uuidMarche, uuidTraitant);
  }

  supprimerMarche(uuid: string): Observable<any> {
    return this.marchesServiceApi.deleteApiMarcheSupprimer(uuid);
  }

  creerAvenant(
    uuidMarche: string,
    fichier: Blob,
    nom: string,
    numero?: string,
    categorie?: string,
    reference?: string,
    dateDebut?: string,
    dateFin?: string
  ): Observable<MarcheDocument> {
    if (!uuidMarche || !fichier || !nom) {
      return throwError((): Error => new Error('uuidMarche, fichier et nom sont requis.'));
    }

    return this.marchesServiceApi
      .postApiPostMarcheAvenant(uuidMarche, fichier, nom, numero, categorie, reference, dateDebut, dateFin, 'body', false, {
        httpHeaderAccept: 'application/json'
      })
      .pipe(map((res: MarcheAvenantOutput): MarcheDocument => MarcheDocument.adapt(res)));
  }

  creerAnnexe(uuidMarche: string, fichier: Blob, nom: string): Observable<MarcheDocument> {
    if (!uuidMarche || !fichier || !nom) {
      return throwError((): Error => new Error('uuidMarche, fichier et nom sont requis.'));
    }
    return this.marchesServiceApi.postApiMarcheAjouterAnnexe(uuidMarche, fichier, nom).pipe(map(res => MarcheDocument.adapt(res)));
  }

  ajouterCatalogue(uuidMarche: string, fichier: Blob, nom: string): Observable<MarcheDocument> {
    if (!uuidMarche || !fichier || !nom) {
      return throwError((): Error => new Error('uuidMarche, fichier et nom sont requis.'));
    }
    return this.marchesServiceApi.postApiMarcheAjouterCatalogue(uuidMarche, fichier, nom).pipe(map(res => MarcheDocument.adapt(res)));
  }

  supprimerCatalogue(uuidMarche: string): Observable<Marche> {
    if (!uuidMarche) {
      return throwError((): Error => new Error('uuidMarche requis.'));
    }
    return this.marchesServiceApi.deleteApiMarcheSupprimerCatalogue(uuidMarche).pipe(map(res => Marche.adapt(res)));
  }

  async creerMarche(): Promise<MarcheOutput2> {
    return new Promise<MarcheOutput2>((resolve, reject) => {
      this.marchesServiceApi.postApiMarcheCreate({} as MarcheInput2).subscribe(
        (marcheOutput: MarcheOutput2): void => {
          resolve(marcheOutput);
        },
        (error: any): void => {
          reject(error);
        }
      );
    });
  }

  /*BPU*/

  enregistrerBpu(marcheUuid: string, fichier: Blob): Observable<MarcheDocument> {
    return this.marchesServiceApi.postApiMarcheBpuImport(marcheUuid, fichier).pipe(map(res => MarcheDocument.adapt(res)));
  }

  recupererBpuList(marcheUuid: string): Observable<MarcheBpu[]> {
    return this.marchesBPUService.getApiMarcheUosList(marcheUuid).pipe(
      map((response: any[]) => {
        if (!response || !Array.isArray(response)) {
          return [];
        }
        return response.map((item: any) => MarcheBpu.adapt(item));
      })
    );
  }

  creerBpu(marcheUuid: string, bpu: Partial<MarcheBpu>): Observable<MarcheBpu> {
    const bpuApi: MarcheBpuInput = {
      libelle: bpu.libelle || '',
      type: bpu.type || ''
    };

    return this.marchesBPUService.postApiMarcheBpuCreate(marcheUuid, bpuApi).pipe(
      map((response: any) => {
        return MarcheBpu.adapt(response);
      })
    );
  }

  recupererBpuUoList(marcheUuid: string): Observable<MarcheBpuUo[]> {
    return this.recupererBpuList(marcheUuid).pipe(
      map((bpus: MarcheBpu[]) => {
        if (bpus && bpus.length > 0) {
          return bpus[0].marcheUos;
        }
        return [];
      })
    );
  }

  ajouterUo(bpuUuid: string, bpuItem: MarcheBpuUo): Observable<MarcheBpuUo> {
    const bpuItemApi: MarcheUoInput2 = {
      reference: bpuItem.reference,
      date: bpuItem.date || new Date().toISOString(),
      designation: bpuItem.designation,
      articleProjet: bpuItem.articleProjet ?? '',
      unite: Number(bpuItem.unite),
      prixHt: Number(bpuItem.prixHt) || 0,
      tva: Number(bpuItem.tva) || 0
    };

    return this.marchesUoService.postApiMarcheUoCreate(bpuUuid, bpuItemApi).pipe(
      map((response: any) => {
        return MarcheBpuUo.adapt(response);
      })
    );
  }

  modifierUo(bpuUuid: string, uo: MarcheBpuUo): Observable<MarcheBpuUo> {
    const marcheUoApi: MarcheUoInput2 = {
      date: uo.date || new Date().toISOString(),
      designation: uo.designation,
      prixHt: Number(uo.prixHt) || 0,
      tva: Number(uo.tva) || 0
    };

    return this.marchesUoService.putApiMarcheUoUpdate(bpuUuid, uo.uuid!, marcheUoApi).pipe(
      map((response: any) => {
        return MarcheBpuUo.adapt(response);
      })
    );
  }

  supprimerUo(bpuUuid: string, bpuItemUuid: string, bpu: MarcheBpu): Observable<any> {
    const bpuApi: MarcheBpuInput = {
      libelle: bpu.libelle || '',
      type: bpu.type || ''
    };
    console.log(bpuApi);
    return this.marchesUoService.deleteApiMarcheUoDelete(bpuUuid, bpuItemUuid).pipe(
      map(() => {
        return true;
      })
    );
  }

  telechargerDocument(documentUuid: string): Observable<Blob> {
    return this.documentsService.getApiDocumentsTelecharger(documentUuid);
  }

  recupererRevisionsBpu(marcheUuid: string, bpuUuid: string): Observable<MarcheBpuRevision[]> {
    return this.marchesBPUService
      .getApiMarcheBpuRevisionList(marcheUuid, bpuUuid)
      .pipe(map((revisions: any[]) => revisions.map(r => MarcheBpuRevision.adapt(r))));
  }

  creerRevisionBpu(marcheUuid: string, bpuUuid: string, dateRevision: string, fichier: Blob): Observable<MarcheBpuRevision> {
    return this.marchesBPUService
      .postApiMarcheBpuRevisionImport(marcheUuid, bpuUuid, fichier, dateRevision)
      .pipe(map((res: any) => MarcheBpuRevision.adapt(res.revision)));
  }

  supprimerRevisionBpu(marcheUuid: string, revisionUuid: string): Observable<any> {
    return this.marchesBPUService.deleteApiMarcheBpuRevisionDelete(marcheUuid, revisionUuid);
  }
}
